import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { getById } from "../../services/firestore";
import { Search, RefreshCw, Check, Clock, Package, Truck, Gift, Percent, XCircle, User as UserIcon, Loader2 } from "lucide-react";

const STATUSES = [
  { v: "pending", label: "Gözləyir",  color: "#c98b4b" },
  { v: "claimed", label: "Təqdim edildi", color: "#2e7ec7" },
  { v: "fulfilled", label: "Yərinə yetirildi", color: "#3aa66d" },
  { v: "expired", label: "Vaxtı keçib",  color: "#7a7a7a" },
];

const TYPE_META = {
  coupon_percent: { icon: Percent, color: "#c53a24", label: "Endirim" },
  free_shipping: { icon: Truck, color: "#2e7ec7", label: "Çatdırılma" },
  gift_item: { icon: Gift, color: "#c98b4b", label: "Hədiyyə" },
  empty: { icon: XCircle, color: "#7a7a7a", label: "Boş" },
};

export default function AdminSpins() {
  const [spins, setSpins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [updating, setUpdating] = useState({});
  const [userCache, setUserCache] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "wheel_spins"), orderBy("created_at", "desc")));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSpins(list);
      // Prefetch user names
      const ids = Array.from(new Set(list.map((s) => s.user_id).filter(Boolean)));
      const map = {};
      await Promise.all(ids.map(async (uid) => {
        try {
          const u = await getById("users", uid);
          if (u) map[uid] = u;
        } catch { /* ignore */ }
      }));
      setUserCache(map);
    } catch (e) {
      console.error("listSpins fail", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (spinId, newStatus) => {
    setUpdating((u) => ({ ...u, [spinId]: true }));
    try {
      await updateDoc(doc(db, "wheel_spins", spinId), {
        status: newStatus,
        updated_at: serverTimestamp(),
      });
      setSpins((all) => all.map((s) => s.id === spinId ? { ...s, status: newStatus } : s));
    } catch (e) {
      alert("Səhv: " + e.message);
    } finally {
      setUpdating((u) => ({ ...u, [spinId]: false }));
    }
  };

  const filtered = spins.filter((s) => {
    if (statusFilter !== "all") {
      const cur = s.status || "pending";
      if (cur !== statusFilter) return false;
    }
    if (typeFilter !== "all" && s.slice_type !== typeFilter) return false;
    if (q) {
      const s2 = q.toLowerCase();
      const u = userCache[s.user_id];
      const hay = [
        s.slice_label, s.order_id, s.user_id,
        u?.email, u?.name, u?.first_name, u?.last_name,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(s2)) return false;
    }
    return true;
  });

  const statusCounts = STATUSES.reduce((acc, st) => {
    acc[st.v] = spins.filter((s) => (s.status || "pending") === st.v).length;
    return acc;
  }, {});
  statusCounts.all = spins.length;

  return (
    <div className="p-4 sm:p-6 md:p-10" data-testid="admin-spins">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-8">
        <div>
          <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium flex items-center gap-2">
            <Gift size={12} className="text-[var(--accent)]" /> Şans Çarxı
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl mt-1">Qazanmalar</h1>
          <div className="text-xs text-[var(--text-muted)] mt-1">Bütün müştəri qazanmalarını buradan izləyin</div>
        </div>
        <button onClick={load} disabled={loading} className="self-start sm:self-end px-3 py-2 border border-[var(--border)] hover:border-[var(--accent)] text-sm inline-flex items-center gap-2">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Yenilə
        </button>
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <FilterChip active={statusFilter === "all"} onClick={() => setStatusFilter("all")} label={`Hamısı (${statusCounts.all || 0})`} />
        {STATUSES.map((s) => (
          <FilterChip
            key={s.v}
            active={statusFilter === s.v}
            onClick={() => setStatusFilter(s.v)}
            color={s.color}
            label={`${s.label} (${statusCounts[s.v] || 0})`}
          />
        ))}
      </div>

      {/* Search + type filter */}
      <div className="flex flex-col sm:flex-row gap-2 mb-6">
        <div className="flex items-center gap-2 border border-[var(--border)] px-3 py-2 flex-1 bg-[var(--bg)]">
          <Search size={14} className="text-[var(--text-muted)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ad→email→sifariş ID axını"
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="input sm:w-56">
          <option value="all">Bütün tiplər</option>
          <option value="coupon_percent">Endirim kuponu</option>
          <option value="free_shipping">Pulsuz çatdırılma</option>
          <option value="gift_item">Fiziki hədiyyə</option>
          <option value="empty">Boş</option>
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--text-muted)]">Yüklənir…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-[var(--border)] bg-[var(--surface)] p-10 text-center text-[var(--text-muted)]">
          <Package size={24} className="mx-auto mb-3" />
          <div>Qazanma yoxdur</div>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((s) => {
            const meta = TYPE_META[s.slice_type] || TYPE_META.empty;
            const Icon = meta.icon;
            const u = userCache[s.user_id] || {};
            const cur = s.status || "pending";
            const dt = s.created_at?.toDate?.() || (s.created_at?.seconds ? new Date(s.created_at.seconds * 1000) : null);
            const isEmpty = s.slice_type === "empty";
            return (
              <div key={s.id} className="border border-[var(--border)] bg-[var(--surface)] p-3 grid grid-cols-1 sm:grid-cols-12 gap-3 items-center" data-testid={`admin-spin-${s.id}`}>
                <div className="sm:col-span-1 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${meta.color}22`, color: meta.color }}>
                    <Icon size={16} />
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <div className="text-[10px] tracking-[0.14em] font-medium" style={{ color: meta.color }}>{meta.label} · #{s.slice_number}</div>
                  <div className="text-sm font-medium">{s.slice_label}</div>
                  {s.percent > 0 && <div className="text-[11px] text-[var(--text-muted)]">{s.percent}% endirim</div>}
                </div>
                <div className="sm:col-span-3 text-sm">
                  <div className="flex items-center gap-1.5 text-[var(--text)]">
                    <UserIcon size={12} className="text-[var(--text-muted)]" />
                    {u.first_name || u.name || u.email || s.user_id?.slice(0, 8) || "—"}
                  </div>
                  {u.email && <div className="text-[11px] text-[var(--text-muted)] truncate">{u.email}</div>}
                </div>
                <div className="sm:col-span-3 text-[11px] text-[var(--text-muted)]">
                  <div>Sifariş: <span className="text-[var(--text)] font-mono">#{s.order_id.slice(0, 10)}</span></div>
                  {dt && <div><Clock size={10} className="inline mr-1" /> {dt.toLocaleString("az")}</div>}
                </div>
                <div className="sm:col-span-2">
                  {isEmpty ? (
                    <div className="text-xs text-[var(--text-muted)] italic">—</div>
                  ) : (
                    <select
                      value={cur}
                      onChange={(e) => updateStatus(s.id, e.target.value)}
                      disabled={!!updating[s.id]}
                      className="input text-xs"
                      style={{ borderColor: STATUSES.find((x) => x.v === cur)?.color + "66" }}
                    >
                      {STATUSES.map((st) => <option key={st.v} value={st.v}>{st.label}</option>)}
                    </select>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, label, color }) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1.5 text-xs border transition-colors"
      style={{
        borderColor: active ? (color || "var(--accent)") : "var(--border)",
        background: active ? (color ? color + "22" : "var(--accent)22") : "transparent",
        color: active ? (color || "var(--accent)") : "var(--text-2)",
      }}
    >
      {label}
    </button>
  );
}
